// B1 — Request Access email confirmation landing (/request-access/verify?token=…).
// The token is read once and stripped from the address bar before the network call (useCapturedParams), then sent in
// a POST body (never a logged URL). Every state shown is the server's actual answer, with a concrete next action.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useCapturedParams } from "../../lib/cleanUrl";

const STATES = {
  verifying: { title: "Confirming your email…", tone: "text-[#12233F]" },
  success: { title: "Email confirmed", tone: "text-emerald-800", next: { to: "/", label: "Return to ERF Enterprises" } },
  used: { title: "Link already used", tone: "text-[#12233F]", next: { to: "/", label: "Return to ERF Enterprises" } },
  expired: { title: "Link expired", tone: "text-[#991B1B]", next: { to: "/request-access", label: "Submit a new access request" } },
  invalid: { title: "Link not valid", tone: "text-[#991B1B]", next: { to: "/request-access", label: "Submit a new access request" } },
  rate_limited: { title: "Too many attempts", tone: "text-[#991B1B]" },
  error: { title: "We couldn't reach ERF", tone: "text-[#991B1B]" },
};
const BY_STATUS = { 409: "used", 410: "expired", 404: "invalid", 400: "invalid", 422: "invalid", 429: "rate_limited" };
const MISSING = "This page needs the confirmation link from your email. Open the most recent link we sent you.";

function NextAction({ state, onRetry }) {
  const cls = "inline-flex min-h-[44px] items-center rounded-sm border border-[#12233F] px-4 text-sm font-semibold transition-colors hover:bg-[#12233F] hover:text-[#F5F1E8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C6A15B]";
  const next = STATES[state].next;
  if (next) return <Link to={next.to} className={cls} data-testid="ra-verify-next-action">{next.label}</Link>;
  if (state === "rate_limited" || state === "error") {
    return <button type="button" onClick={onRetry} className={cls} data-testid="ra-verify-retry">Try again</button>;
  }
  return null;
}

export default function RequestAccessVerify() {
  const { token } = useCapturedParams();
  const [state, setState] = useState(token ? "verifying" : "invalid");
  const [message, setMessage] = useState(token ? "" : MISSING);
  const once = useRef(false);

  const verify = useCallback(() => {
    if (!token) return;
    setState("verifying");
    setMessage("");
    api.post("/public/access-requests/verify", { token })
      .then((r) => { setState("success"); setMessage(r.data.message); })
      .catch((e) => {
        const s = BY_STATUS[e?.response?.status] || "error";
        setState(s);
        setMessage(e?.response?.data?.detail?.message || "Check your connection and try again in a moment.");
      });
  }, [token]);

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    verify();
  }, [verify]);

  return (
    <main className="min-h-screen bg-[#F5F1E8] px-6 py-16 text-[#12233F]" data-testid="ra-verify-page" data-state={state}>
      <div className="mx-auto max-w-xl border-t-4 border-[#C6A15B] pt-6">
        <p className="font-mono text-[11px] uppercase tracking-[2px] text-[#8A6D35]">ERF ConstructionOS · Portal Access</p>
        <h1 className={`mt-2 font-display text-4xl font-semibold ${STATES[state].tone}`} data-testid="ra-verify-title">
          {STATES[state].title}
        </h1>
        <p className="mt-4 text-base leading-relaxed" role={state === "verifying" ? "status" : "alert"} data-testid={`ra-verify-${state}`}>
          {state === "verifying" ? "Please wait while we confirm your link." : message}
        </p>
        <div className="mt-8"><NextAction state={state} onRetry={verify} /></div>
      </div>
    </main>
  );
}
